<template>
  <b-card>
    <app-data-table
      :items="providerList"
      :fields="tableColumns"
      :empty-text="$t('common.no_record_found')"
      :sort-by.sync="sortBy"
      :per-page="params.numberOfItemsPerPage"
      :bottom-table="{ totalCount: totalItems }"
      :page="params.page"
      manual-pagination
      @pagination="params.page = $event"
    >
      <template #head(status)>
        <feather-icon class="mx-auto" icon="TrendingUpIcon" />
      </template>

      <template #head(basePrice)>
        {{ $t('trip.fuel.base_price_et') }}
      </template>

      <!-- Column: Seller -->
      <template #cell(seller)="data">
        <b-avatar :src="data.item.organizationLogoUrl" :text="avatarText(data.item.organizationName)" size="32" class="d-inline-block mr-2" />

        <b-media vertical-align="center" class="d-inline-block">
          <span class="font-weight-bold d-block text-nowrap text-underline">
            <b-link
              :to="{
                name: 'provider-details',
                params: {
                  provider_id: data.item.organizationId,
                  trip_id: tripData.id,
                  step_id: tripStep.id,
                  event_id: tripStepEvent.id,
                  airport_id: tripStepEvent.airport.id,
                },
                query: {
                  currentNav: 'fuel'
                },
              }"
            >
              {{ data.item.organizationName }}
            </b-link>
          </span>
          <small class="text-muted">{{ data.item.flightType }}</small>
        </b-media>
      </template>

      <!-- Column: Supplier -->
      <template #cell(supplier)="data">
        <b-media vertical-align="center">
          <span class="font-weight-bold d-block text-nowrap">
            {{ data.item.supplier }}
          </span>
        </b-media>
      </template>

      <!-- Column: Contract -->
      <template #cell(contract)="data">
        <b-badge :variant="`light-${resolveStatusVariant(data.item.contract)}`" class="mx-1">
          {{ data.item.contract ? $t('trip.fuel.contract') : $t('trip.fuel.no_contract') }}
        </b-badge>
      </template>

      <!-- Column: Base Price -->
      <template #cell(basePrice)="data">
        <span class="text-nowrap">
          {{ $t('trip.fuel.base_price_et') }} :
          <span v-if="data.item.unitBasePrice"> {{ data.item.unitBasePrice }} {{ $getMeasurementUnitItn('epl') }}</span>
          <span v-else-if="!data.item.public"> {{ $t('service.fuel.prices.private_price') }} </span>
          <span v-else> {{ $t('service.on_quote') }} </span>
        </span>
      </template>

      <!-- Column: Fuel type -->
      <template #cell(fuelType)="data">
        <span class="text-nowrap">
          <span v-if="data.item.fuelType"> {{ data.item.fuelType }} </span>
        </span>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <b-button
          class="mr-1"
          @click="
            isContactFuelProviderSidebarActive = true
            providerSelected = data.item
          "
        >
          <font-awesome-icon class="mr-50" icon="comment-dots" />
          {{ $t('action.contact') }}
        </b-button>
        <b-badge
          v-if="false && eventData.providers.some(provider => provider.provider.id === data.item.id && data.item.status === 'Pending')"
          variant="light-warning"
        >
          {{ $t('timeline.fuel.request_quotation.sent') }}
        </b-badge>
        <b-button
          v-else
          @click="
            isRequestQuotationSidebarActive = true
            providerSelected = data.item
            fuelTypeName = data.item.fuelType
          "
        >
          <font-awesome-icon class="mr-50" icon="file-invoice-dollar" />
          {{ $t('trip.fuel.request_quotation') }}
        </b-button>
      </template>
    </app-data-table>
    <sidebar-request-quotation v-model="isRequestQuotationSidebarActive" :fuel-settings="fuelSettings" :provider-fuel="providerSelected" :trip-data="tripData" :trip-step="tripStep" :trip-step-event="tripStepEvent" />
    <sidebar-contact-fuel-provider v-model="isContactFuelProviderSidebarActive" :provider-fuel="providerSelected" />
  </b-card>
</template>

<script>
import UIHelper from '@/helpers/ui'
import SidebarRequestQuotation from '@/views/provider/fuel/sidebar/SidebarRequestQuotation.vue'
import SidebarContactFuelProvider from '@/views/provider/fuel/sidebar/SidebarContactFuelProvider.vue'
import AppDataTable from '@/components/AppDataTable.vue'

export default {
  name: 'FuelProvider',

  components: {
    AppDataTable,
    SidebarContactFuelProvider,
    SidebarRequestQuotation,
  },
  mixins: [UIHelper],
  props: {
    tripData: {
      type: Object,
      default: () => ({}),
    },
    tripStep: {
      type: Object,
      default: () => ({}),
    },
    tripStepEvent: {
      type: Object,
      default: () => ({}),
    },
    providerList: {
      type: Array,
      default: () => [],
    },
    fuelTypeName: {
      type: String,
      default: null,
    },
    fuelVolume: {
      type: Number,
      default: 0,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      fuelMeasurementUnity: 'litres',
      measurementOptions: [{ label: 'Liters', value: 'litres' }],
      isRequestQuotationSidebarActive: false,
      isContactFuelProviderSidebarActive: false,
      providerSelected: null,

      tableColumns: [
        { key: 'seller' },
        { key: 'supplier' },
        { key: 'contract' },
        { key: 'basePrice' },
        { key: 'fuelType' },
        { key: 'actions' },
      ],
      statusOptions: ['Completed', 'In progress', 'Action required'],
      products: [],
      loadProducts: false,
      itemView: 'grid-view',
      itemViewOptions: [
        { icon: 'GridIcon', value: 'grid-view' },
        { icon: 'ListIcon', value: 'list-view' },
      ],
      params: {
        // PAGE
        numberOfItemsPerPage: 5,
        page: 1,
        // SORTING
        nameOrder: null,
        // SEARCH
        searchQuery: null,
        categoriesIds: null,
      },
      perPageOptions: [5, 10, 25, 50, 100],
      sortBy: null,
      sortDesc: null,
    }
  },
  computed: {
    userLang() {
      return this.$store.state.appConfig.lang
    },
    fuelSettings() {
      return { fuelTypeName: this.fuelTypeName, quantity: this.fuelVolume, message: '', dateFuelRequested: this.tripStepEvent ? this.tripStepEvent.eventDateTime : null }
    },
    dataMeta() {
      const localItemsCount = this.products.length
      return {
        from: this.params.numberOfItemsPerPage * (this.params.page - 1) + (localItemsCount ? 1 : 0),
        to: this.params.numberOfItemsPerPage * (this.params.page - 1) + localItemsCount,
        of: this.totalItems,
      }
    },
    allFilters() {
      return {
        numberOfItemsPerPage: this.params.numberOfItemsPerPage,
        page: this.params.page,
        // SORTING
        nameOrder: this.params.nameOrder,
        // SEARCH
        searchQuery: this.params.searchQuery,
        categoriesIds: this.params.categoriesIds,
      }
    },
  },
  watch: {
    allFilters: {
      handler() {
        this.$emit('refetch-data', this.params.page)
      },
      deep: true,
    },
    fuelSettings() {
      // ! Never used !?
      this.$emit('fuel-settings', this.fuelSettings)
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.trip-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>
